// src/logic/RocksLayer.jsx
import React, { memo } from "react";

/**
 * Props:
 *  - rocks: array of { id, type, x, y, collected, item_type }
 *  - camX, camY: camera offsets
 *  - breakingFrames: map from rock.id to frame index (0–3)
 *  - onRockClick: callback when you click a rock
 */
const animatedTypes = ["rock","gold","emerald","ruby","diamond","sapphire"];


// Map item types to their image paths
const itemImagePaths = {
  "ghost_shard": "/items/Ghost%20Shard.png",
  "dark_shard": "/items/Dark%20Shard.png",
  "blue_shard": "/items/blueshard.png",
  "green_shard": "/items/Green%20Shard.png",
  "yellow_shard": "/items/yellowshard.png",
  "red_shard": "/items/Red%20Shard.png",
  "ruby": "/items/ruby.png",
  "sapphire": "/items/sapphire.png",
  "emerald": "/items/emerald.png",
  "gold": "/items/gold.png",
  "diamond": "/items/diamond.png",
  "potion": "/items/potion.png",
  "lure": "/items/lure.png",
  "super_lure": "/items/superlure.png",
  "ultra_lure": "/items/ultralure.png",
  "super_potion": "/items/superpotion.png",
  "hyper_potion": "/items/hyperpotion.png",
  "dragon_fang": "/items/dragonfang.png",
  "mining_lure": "/items/mininglure.png",
  "safe": "/items/safe.png",
  "revive": "/items/revive.png",
  "max_revive": "/items/maxrevive.png",
  "mighty_candy": "/items/MightyCandy.png",
  "courage_candy": "/items/CourageCandy.png",
  "health_candy": "/items/HealthCandy.png",
  "quick_candy": "/items/QuickCandy.png",
  "smart_candy": "/items/SmartCandy.png",
  "tough_candy": "/items/ToughCandy.png",
};

// Fallback image for items without specific images
const defaultItemImage = "/items/pokeball.png";

const RocksLayer = memo(function RocksLayer({
  rocks,
  camX,
  camY,
  onRockClick,
  breakingFrames = {}
}) {
  return (
    <>
      {rocks
        .filter(r => !r.collected)
        .map(r => {
          // frame 0–3
          const frame = breakingFrames[r.id] ?? 0;
          
          // Check if this is an Item Finder item
          const isItemFinderItem = r.item_type && r.item_type.startsWith('item_tier');
          
          let imgPath;
          if (isItemFinderItem) {
            // Extract the actual item type from the item_type field (e.g., "ghost_shard" from "item_tier1_ghost_shard")
            const itemType = r.item_type.split('_').slice(2).join('_');
            // Use the specific item image if available, otherwise use default
            imgPath = itemImagePaths[itemType] || defaultItemImage;
          } else {
            // Regular rock/gem path
            imgPath = `/Maps/rocks/${r.type}.png`;
          }
          
          // Only apply animation to rocks, not to items
          const isAnimated = !isItemFinderItem && animatedTypes.includes(r.type);
          const bgSize = isAnimated ? `${16 * 4}px 16px` : "16px 16px";
          const bgPos = isAnimated ? `-${frame * 16}px 0px` : "0px 0px";

          // Add a subtle floating animation for items
          const animation = isItemFinderItem ? "floatItem 2s infinite ease-in-out" : "none";

          const style = {
            position:           "absolute",
            left:               r.x - camX,
            top:                r.y - camY,
            width:              "16px",
            height:             "16px",
            backgroundImage:    `url(${imgPath})`,
            backgroundSize:     bgSize,
            backgroundPosition: bgPos,
            cursor:             "pointer",
            zIndex:             100,
            animation:          animation,
            // Add a subtle glow effect for items
            filter:             isItemFinderItem ? "drop-shadow(0 0 3px gold)" : "none"
          };

          return (
            <div
              key={r.id}
              style={style}
              onClick={() => onRockClick?.(r)}
              title={isItemFinderItem ? "Item Finder Discovery" : "Rock"}
            />
          );
        })}

      {/* Add CSS for the floating animation */}
      <style jsx>{`
        @keyframes floatItem {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>
    </>
  );
});

export default RocksLayer;
